import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { defaultWidgetIdFactory as idFactory, assertUsableWidgetId } from "../ids.js";
import { MDY_FIELD_SHELL_CLASSES, MDY_FIELD_STATE_CLASSES } from "../structure.js";
import { MDY_WIDGET_KINDS } from "../catalog.js";
import { widgetSupportsState } from "../widget-states.js";
import { projectFieldShellA11y } from "./shell-a11y.js";
import { errorsVisible, holdsUneditedValue } from "./verdict.js";
/** Builds the static IDs used by a field widget view. */
export function textFieldPartIds(widgetId) {
    assertUsableWidgetId(widgetId);
    return {
        inputId: widgetId,
        labelId: idFactory.part(widgetId, "label"),
        descriptionId: idFactory.part(widgetId, "description"),
        errorId: idFactory.part(widgetId, "errors"),
    };
}
/** Computes the public state classes for the field root. */
export function textFieldRootClasses(state) {
    const S = MDY_FIELD_STATE_CLASSES;
    return [
        S.field,
        ...S.fieldStates
            .filter((name) => Boolean(state[name]))
            .map((name) => `${S.field}--${name}`),
    ];
}
/** Projects ARIA attributes and classes for the field parts. */
export function projectTextFieldA11y(state, errors, options) {
    const { inputId, labelId, descriptionId, errorId } = textFieldPartIds(options.widgetId);
    // Out of play, no verdict — the wrapper, the label, `aria-invalid` and whether the error
    // text renders are four faces of one question, answered once in verdict.ts.
    // Whether the person is being told yet. A rule they have not answered waits for them to reach the
    // field; a refusal about the value already there does not, because they can neither cause it by
    // inaction nor see the reason unless it is said.
    const tellingThem = errorsVisible({ disabled: state.disabled, touched: state.touched, holdsUnedited: holdsUneditedValue(state, options.kind) }, errors);
    // Whether this kind admits read-only at all, asked of the contract. A kind this contract does not
    // know is not this contract's to police: a consumer rendering their own kind keeps what they had.
    const kind = options.kind;
    const announcesReadonly = kind === undefined
        || !MDY_WIDGET_KINDS.includes(kind)
        || widgetSupportsState(kind, "readonly");
    // And whether the platform acts on the attribute, which is a different question. HTML honours
    // `readonly` on a text-entry control and ignores it on a range — so a slider announces read-only
    // and does not bind it: an attribute the browser drops is the appearance of a guarantee with
    // nothing behind it, and what refuses the drag is the renderer asking `blocksValueChange`.
    const bindsNativeReadonly = announcesReadonly && kind !== "slider";
    return {
        root: {
            classes: textFieldRootClasses(state),
            attributes: {},
        },
        label: {
            id: labelId,
            classes: [MDY_FIELD_SHELL_CLASSES.label],
            attributes: {
                for: inputId,
            },
        },
        input: {
            id: inputId,
            classes: [],
            attributes: {
                // What the control is, and what this projection alone knows: the kind's input type, the
                // autocomplete a caller asked for, and the two native flags.
                // No invented default: a kind whose control type the contract does not declare leaves the
                // attribute off rather than being told it is a text box. The implicit `?? "text"` put
                // `type="text"` on a <textarea> — an attribute that element does not have — because a
                // default nobody declared cannot have an exception.
                type: options.inputType ?? null,
                autocomplete: options.autocomplete ?? null,
                // Everything a control exposes about its *state and rules* comes from the shell projection,
                // which is where a renderer that binds a part reads it. Two projections spelling the same
                // attributes is how they come to disagree — so this one asks rather than repeats.
                ...projectFieldShellA11y({ disabled: state.disabled, required: state.required }, errors, {
                    widgetId: options.widgetId,
                    kind: options.kind ?? options.inputType,
                    constraints: options.constraints,
                    errorsVisible: tellingThem,
                    // The container is pointed at while it is on the page, not while it holds a message: a
                    // renderer that reserves it keeps one reference that never changes.
                    errorsReserved: options.errorsReserved ?? tellingThem,
                    supportingText: options.supportingText ?? null,
                    // Asked of the shell rather than written here: this projection spreads the shell's
                    // attributes over its own, so a name written on both sides is decided by the spread
                    // rather than by either author.
                    submitName: options.submitName,
                }).control.attributes,
                // The shell has no notion of read-only: it is a state only some kinds admit, and the field
                // projection is the one that knows whether this control does — asked of the contract rather
                // than assumed from the file the projection lives in. A slider is drawn by this same
                // projection and declares no read-only state, so it was announcing one: the state belongs to
                // the kind, not to the function that happens to draw it.
                "aria-readonly": announcesReadonly && state.readonly ? "true" : null,
                inputmode: options.inputMode ?? options.constraints?.inputMode ?? null,
                disabled: state.disabled,
                readonly: bindsNativeReadonly && state.readonly,
            },
        },
        description: {
            id: descriptionId,
            classes: [MDY_FIELD_SHELL_CLASSES.supportingText],
            attributes: {},
            ...(typeof options.supportingText === "string" && options.supportingText !== ""
                ? { content: { text: options.supportingText } } : {}),
        },
        error: {
            id: errorId,
            classes: [MDY_FIELD_SHELL_CLASSES.errors],
            attributes: {
                // A live region and nothing more. `role="alert"` here would override the list semantics of
                // the <ul> it sits on: axe reports every <li> inside such a list as an orphaned list item, and
                // a screen reader sees the same thing. `aria-live` announces the list when it appears, so the
                // role would cost the structure and add nothing.
                "aria-live": "polite",
            },
        },
        // The readout beside a slider, where the kind declares one. A number a person can see is the
        // whole reason that part exists, and every renderer that drew it decided for itself what it
        // said — one of them said nothing, because the part reached it as a shape with no content.
        ...(kind === "slider"
            ? {
                value: {
                    classes: [...MDY_WIDGET_CONTRACTS.slider.parts["value"]?.classes ?? []],
                    attributes: {},
                    content: { text: String(state.value ?? "") },
                },
            }
            : {}),
    };
}
